// The feedback button's output: one block of text Gabriel pastes into Claude
// Code. Everything Claude would otherwise ask first (which page, which file,
// what was open, what he clicked, what the server said) is already in it.
//
// Pure, so test/feedback-prompt.test.js can pin the wording without a browser.

export const KINDS = [
  { id: "bug", label: "Bug", hint: "What happened, and what did you expect instead?" },
  { id: "missing", label: "Missing", hint: "What should be here that isn't?" },
  { id: "idea", label: "Idea", hint: "What would make this better?" },
];

// What each kind asks Claude to do with it.
const ASK = {
  bug:
    "This is a bug. Reproduce it from the trail above, find the root cause, fix it with a test, and open a PR.",
  missing:
    "This is missing functionality. First check the code (on origin/main, not a stale checkout) that it really isn't there, maybe behind a setting or on another page. If it isn't, build it with tests and open a PR. If it needs a screen that doesn't exist yet, describe the screen and stop for my mockup before building UI.",
  idea:
    "This is an idea, not a build order. Look at how it would fit with what's there, then come back with a short proposal: what changes, which files it touches, and the questions I need to answer. Don't write code yet.",
};

// Route pattern → the file that draws it, so Claude starts in the right place.
// test/feedback-prompt.test.js fails if App.js gains a route this list lacks.
export const ROUTE_FILES = [
  ["/today", "client/src/crm/TodayPage.js", "Today"],
  ["/", "client/src/crm/BoardPage.js", "Pipeline board"],
  ["/approvals", "client/src/crm/ApprovalsPage.js", "Approvals"],
  ["/leads", "client/src/crm/LeadsListPage.js", "Leads"],
  ["/lists", "client/src/crm/lists/ListsIndexPage.js", "Lists"],
  // Before /lists/:id: matchRoute takes the first pattern that fits.
  ["/lists/check", "client/src/crm/lists/CheckPeoplePage.js", "Check these people"],
  ["/lists/:id", "client/src/crm/lists/ListPage.js", "List"],
  ["/sales/rooms", "client/src/crm/rooms/RoomsPage.js", "Prospect rooms"],
  ["/sales/library", "client/src/crm/rooms/LibraryPage.js", "Decks & examples"],
  ["/follow-ups", "client/src/crm/FollowUpsPage.js", "Follow-ups"],
  ["/follow-ups/rules/:id", "client/src/crm/RuleBuilderPage.js", "Follow-up rule"],
  ["/automations", "client/src/crm/AutomationsPage.js", "Automations"],
  ["/inbound/automations", "client/src/crm/AutomationsPage.js", "Inbound automations"],
  ["/meetings/automations", "client/src/crm/AutomationsPage.js", "Meeting automations"],
  ["/meetings/availability", "client/src/crm/AvailabilityPage.js", "Availability"],
  ["/inbound/scoring", "client/src/crm/ScoringPage.js", "Lead scoring"],
  ["/inbound/landing-pages", "client/src/crm/landing/LandingPagesPage.js", "Landing pages"],
  ["/inbound/analytics", "client/src/crm/analytics/AnalyticsPage.js", "Analytics"],
  ["/meetings/calendar", "client/src/crm/CalendarPage.js", "Calendar"],
  ["/meetings/calls", "client/src/crm/calls/CallsPage.js", "Calls"],
  ["/meetings/calls/:id", "client/src/crm/calls/CallsPage.js", "Call"],
  ["/automations/:id", "client/src/crm/AutomationDetailPage.js", "Automation"],
  ["/booking-page", "client/src/crm/BookingAdminPage.js", "Booking page admin"],
  ["/contacts", "client/src/crm/Placeholder.js", "Contacts"],
  ["/contacts/:id", "client/src/crm/ContactDetailPage.js", "Contact"],
  ["/reports", "client/src/crm/ReportsPage.js", "Reports"],
  ["/email-queue", "client/src/pages/LeadsPage.js", "Email queue"],
  ["/settings", "client/src/crm/SettingsPage.js", "Settings"],
];

export function matchRoute(pathname) {
  const parts = String(pathname || "/").replace(/\/+$/, "").split("/");
  for (const [pattern, file, name] of ROUTE_FILES) {
    const want = pattern.replace(/\/+$/, "").split("/");
    if (want.length !== parts.length) continue;
    if (want.every((w, i) => w.startsWith(":") || w === parts[i])) return { pattern, file, name };
  }
  return null;
}

// Tokens ride in some query strings (the SSE stream); never paste them anywhere.
const SECRET_PARAM = /^(token|key|secret|password|code|auth.*)$/i;

export function cleanUrl(url) {
  let u;
  try {
    u = new URL(String(url), "http://x");
  } catch {
    return String(url).slice(0, 200);
  }
  for (const k of [...u.searchParams.keys()]) if (SECRET_PARAM.test(k)) u.searchParams.set(k, "…");
  const q = u.searchParams.toString();
  return (u.pathname + (q ? `?${decodeURIComponent(q)}` : "")).slice(0, 200);
}

const pad = (n) => String(n).padStart(2, "0");
export const clock = (ms) => {
  const d = new Date(ms);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export function stamp(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

export function screenshotNames(count, at) {
  const base = `crm-feedback-${stamp(at)}`;
  return Array.from({ length: count }, (_, i) => `${base}${count > 1 ? `-${i + 1}` : ""}.png`);
}

function actionLine(e) {
  if (e.kind === "route") return `${clock(e.at)} opened ${e.text}`;
  if (e.kind === "click") return `${clock(e.at)} clicked ${e.text}`;
  if (e.kind === "edit") return `${clock(e.at)} changed ${e.text}`;
  if (e.kind === "key") return `${clock(e.at)} pressed ${e.text}`;
  return `${clock(e.at)} ${e.text}`;
}

const requestLine = (r) =>
  `${clock(r.at)} ${r.method} ${r.path} → ${r.status || "no response"}${r.ms != null ? ` (${r.ms}ms)` : ""}${r.error ? ` ${r.error}` : ""}`;

/**
 * @param {object} p
 * @param {string} p.kind        bug | missing | idea
 * @param {string} p.message     what Gabriel wrote
 * @param {object} p.where       snapshot of the screen (see FeedbackButton.whereNow)
 * @param {Array}  p.actions     trail: route/click/edit/key events, oldest first
 * @param {Array}  p.requests    API calls, oldest first
 * @param {Array}  p.errors      page errors, oldest first
 * @param {string[]} p.screenshots  filenames saved to Downloads
 * @param {number} p.at          when the report was made (ms)
 */
export function buildPrompt({ kind, message, where = {}, actions = [], requests = [], errors = [], screenshots = [], at }) {
  const k = KINDS.find((x) => x.id === kind) || KINDS[0];
  const route = matchRoute(where.path);
  const lines = [`## CRM feedback: ${k.label}`, ""];

  const text = String(message || "").trim();
  lines.push(...(text ? text.split("\n").map((l) => `> ${l}`) : ["> (no description)"]), "");

  lines.push("### Where I was");
  const url = where.path ? `\`${where.path}${where.search || ""}\`` : "unknown";
  lines.push(route ? `- Page: ${route.name} (${url}), drawn by \`${route.file}\`` : `- Page: ${url}`);
  if (where.heading && (!route || where.heading !== route.name)) lines.push(`- Heading: ${where.heading}`);
  if (where.record) lines.push(`- Record open: ${where.record} (record panel, \`client/src/crm/record/\`)`);
  for (const d of where.dialogs || []) lines.push(`- Dialog open: ${d}`);
  if (where.selection) lines.push(`- Text I had selected: "${where.selection}"`);
  const screen = [where.viewport, where.mobile ? "phone layout" : null, where.build && `build ${where.build}`].filter(Boolean);
  if (screen.length) lines.push(`- Screen: ${screen.join(", ")}`);
  lines.push(`- When: ${where.when || new Date(at).toString()}`);
  if (screenshots.length === 1) {
    lines.push(`- Screenshot: saved to my Downloads folder as \`${screenshots[0]}\`. Open it and look before you start.`);
  } else if (screenshots.length > 1) {
    lines.push(`- Screenshots: saved to my Downloads folder as ${screenshots.map((s) => `\`${s}\``).join(", ")}. Open them and look before you start.`);
  }
  lines.push("");

  if (actions.length) {
    lines.push("### What I did just before (oldest first)", ...actions.map((e) => `- ${actionLine(e)}`), "");
  }
  if (errors.length) {
    lines.push("### Errors on the page", ...errors.map((e) => `- ${clock(e.at)} ${e.text}`), "");
  }
  const failed = requests.filter((r) => !r.status || r.status >= 400);
  if (failed.length) {
    lines.push("### Requests that failed", ...failed.map((r) => `- ${requestLine(r)}`), "");
  }
  const recent = requests.slice(-12);
  if (recent.length) {
    lines.push("### Last API calls", ...recent.map((r) => `- ${requestLine(r)}`), "");
  }

  lines.push("### What I want", `${ASK[k.id]} The repo is gabremotion/remotion-crm (live at crm.remotion.io).`);
  return lines.join("\n");
}
